// Situação, tipo e datas de um desconto, derivados do registro (GET /discounts). Funções puras.
//
// Ninguém vira "encerrado" sozinho no banco: `isEnabled` só vira false por "Desativar" ou quando um
// novo Padrão assume o posto (sem `disabledAt`). Quem passou do `endAt` continua `isEnabled=true`,
// então a situação sai das datas, igual ao filtro `status` do backend (ativo, agendado, encerrado,
// desativado).
//
// Datas são sempre de Brasília (UTC-3 fixo, sem horário de verão desde 2019): é o fuso em que o
// desconto é gravado (`-03:00`) e avaliado na bomba. Nenhum cálculo depende do fuso do navegador.
import { getAppLocale } from '../../../i18n/locale'
import { __t } from '../../../i18n/t'
import { DiscountFrequency } from '../../../models/discount'

export enum DiscountTypeKey {
  default = 'default',
  special = 'special',
  reward = 'reward',
  recovery = 'recovery',
}

/** Valores de `filter.status` do GET /discounts. */
export enum DiscountStatusKey {
  active = 'active',
  scheduled = 'scheduled',
  ended = 'ended',
  disabled = 'disabled',
}

export enum StatusTone {
  success = 'success',
  info = 'info',
  neutral = 'neutral',
  error = 'error',
}

export interface DayWindow {
  /** 0 = domingo ... 6 = sábado */
  dayOfTheWeek: number
  startTime?: string | Date | null
  endTime?: string | Date | null
}

export interface DiscountLike {
  type?: string | null
  isDefault?: boolean | null
  isEnabled?: boolean | null
  disabledAt?: string | Date | null
  startAt?: string | Date | null
  endAt?: string | Date | null
  frequency?: string | null
  /** Vem no GET por id e na lista (o backend a anexa a cada página, junto com `filter.status`). */
  dayScheduledDiscounts?: DayWindow[] | null
}

export interface DiscountStatus {
  key: DiscountStatusKey
  tone: StatusTone
  text: string
}

// ─── Idioma, números e datas de Brasília ────────────────────────────────────────

const BR_OFFSET_MS = 3 * 3600 * 1000
const pad = (n: number) => String(n).padStart(2, '0')

export const intlLocale = () => (getAppLocale() === 'es' ? 'es' : 'pt-BR')
// Um formatador só para a lista e o cadastro: o painel mostra número no formato pt-BR nos dois idiomas
// (só textos e calendários mudam), igual ao resto do admin.
export { fmtN } from '../v2/format'

/** "A, B e C" (em espanhol, "A, B y C"). */
export const listText = (items: string[], type: 'conjunction' | 'disjunction' = 'conjunction') => {
  if (items.length <= 1) return items[0] || ''
  try {
    return new Intl.ListFormat(intlLocale(), { style: 'long', type }).format(items)
  } catch {
    return items.join(', ')
  }
}

const toDate = (value?: string | Date | null): Date | null => {
  if (!value) return null
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

/** Lê-se o resultado com getUTC*: o relógio de Brasília dentro de um Date em UTC. */
const inBrasilia = (date: Date) => new Date(date.getTime() - BR_OFFSET_MS)

/** "sáb", "dom"... (minúsculas, sem ponto, no idioma da tela). 0 = domingo. */
export const dowShort = (dow: number) =>
  new Intl.DateTimeFormat(intlLocale(), { weekday: 'short', timeZone: 'UTC' })
    .format(new Date(Date.UTC(2000, 0, 2 + dow)))
    .replace(/\.$/, '')
    .toLowerCase()

/** "17/10" */
export const dayMonth = (value?: string | Date | null) => {
  const date = toDate(value)
  if (!date) return ''
  const b = inBrasilia(date)
  return `${pad(b.getUTCDate())}/${pad(b.getUTCMonth() + 1)}`
}

/** "17/10/2026" */
export const dayMonthYear = (value?: string | Date | null) => {
  const date = toDate(value)
  return date ? `${dayMonth(date)}/${inBrasilia(date).getUTCFullYear()}` : ''
}

/** "sáb, 17/10"; com o ano ("sex, 29/11/2025") quando não é o ano de `now`. */
export const dayLabel = (value?: string | Date | null, now: Date = new Date()) => {
  const date = toDate(value)
  if (!date) return ''
  const b = inBrasilia(date)
  const year = b.getUTCFullYear() === inBrasilia(now).getUTCFullYear() ? '' : `/${b.getUTCFullYear()}`
  return `${dowShort(b.getUTCDay())}, ${dayMonth(date)}${year}`
}

/** "18:00" */
export const clockStr = (value?: string | Date | null) => {
  const date = toDate(value)
  if (!date) return ''
  const b = inBrasilia(date)
  return `${pad(b.getUTCHours())}:${pad(b.getUTCMinutes())}`
}

// ─── Tipo ───────────────────────────────────────────────────────────────────────

export const typeKey = (d: DiscountLike): DiscountTypeKey => {
  switch (d.type) {
    case DiscountTypeKey.default: return DiscountTypeKey.default
    case DiscountTypeKey.reward: return DiscountTypeKey.reward
    case DiscountTypeKey.recovery: return DiscountTypeKey.recovery
    case DiscountTypeKey.special: return DiscountTypeKey.special
    default: return d.isDefault ? DiscountTypeKey.default : DiscountTypeKey.special
  }
}

export const typeLabel = (key: DiscountTypeKey): string => {
  switch (key) {
    case DiscountTypeKey.default: return __t('Padrão')
    case DiscountTypeKey.reward: return __t('Recompensa')
    case DiscountTypeKey.recovery: return __t('Recuperação')
    default: return __t('Especial')
  }
}

// ─── Dias e horários ────────────────────────────────────────────────────────────

const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0]

const onlyOn = (dow: number): string => {
  switch (dow) {
    case 0: return __t('só aos domingos')
    case 1: return __t('só às segundas')
    case 2: return __t('só às terças')
    case 3: return __t('só às quartas')
    case 4: return __t('só às quintas')
    case 5: return __t('só às sextas')
    default: return __t('só aos sábados')
  }
}

const windowOf = (row: DayWindow) => {
  const from = clockStr(row.startTime)
  const to = clockStr(row.endTime)
  // "O dia todo" é gravado como 00:00-23:59; sem horário nenhum também vale o dia inteiro.
  return { from, to, allDay: (!from && !to) || (from === '00:00' && to === '23:59') }
}

/** Tem janela de dias/horários (semanal ou diária), seja qual for. */
export const hasScheduleWindows = (d: DiscountLike) => d.frequency === DiscountFrequency.Weekly || d.frequency === DiscountFrequency.Daily

/**
 * Quando o desconto vale dentro do período: "só às terças", "todo dia, das 18:00 às 22:00".
 * Vazio quando vale o dia todo, todo dia. Sem as janelas no registro (`dayScheduledDiscounts` ausente),
 * só dá para dizer que há dias e horários definidos. Com a lista vazia, não há janela nenhuma: quem
 * explica é o aviso do cartão ("nunca vale"), não este texto.
 */
export const scheduleQualifier = (d: DiscountLike): string => {
  const rows = (d.dayScheduledDiscounts || []).filter(row => row && Number.isInteger(row.dayOfTheWeek) && row.dayOfTheWeek >= 0 && row.dayOfTheWeek <= 6)
  if (!rows.length) {
    return !Array.isArray(d.dayScheduledDiscounts) && hasScheduleWindows(d) ? __t('só em dias e horários definidos') : ''
  }

  const days = WEEK_ORDER.filter(dow => rows.some(row => row.dayOfTheWeek === dow))
  const windows = rows.map(windowOf)
  const same = windows.every(w => w.from === windows[0].from && w.to === windows[0].to)
  let hours = ''
  if (!same) hours = __t('em horários diferentes a cada dia')
  else if (!windows[0].allDay) hours = __t('das %{inicio} às %{fim}', { inicio: windows[0].from, fim: windows[0].to })

  const everyDay = days.length === 7
  if (everyDay && !hours) return ''
  let when: string
  if (everyDay) when = __t('todo dia')
  else if (days.length === 1) when = onlyOn(days[0])
  else when = __t('só em %{dias}', { dias: listText(days.map(dowShort)) })
  return hours ? `${when}, ${hours}` : when
}

// ─── Situação ───────────────────────────────────────────────────────────────────

export const getDiscountStatus = (d: DiscountLike, now: Date = new Date()): DiscountStatus => {
  if (d.isEnabled === false) {
    const disabledAt = toDate(d.disabledAt)
    // Padrão trocado por outro: o backend desliga sem `disabledAt` (só "Desativar" grava a data).
    if (typeKey(d) === DiscountTypeKey.default && !disabledAt) {
      return { key: DiscountStatusKey.disabled, tone: StatusTone.neutral, text: __t('Substituído por outro Padrão') }
    }
    return {
      key: DiscountStatusKey.disabled,
      tone: StatusTone.error,
      text: disabledAt ? __t('Desativado em %{dia}', { dia: dayMonth(disabledAt) }) : __t('Desativado'),
    }
  }

  const endAt = toDate(d.endAt)
  if (endAt && endAt.getTime() < now.getTime()) {
    return { key: DiscountStatusKey.ended, tone: StatusTone.neutral, text: __t('Terminou %{dia}', { dia: dayLabel(endAt, now) }) }
  }
  const startAt = toDate(d.startAt)
  if (startAt && startAt.getTime() > now.getTime()) {
    return { key: DiscountStatusKey.scheduled, tone: StatusTone.info, text: __t('Começa %{dia}', { dia: dayLabel(startAt, now) }) }
  }

  const qualifier = scheduleQualifier(d)
  return {
    key: DiscountStatusKey.active,
    tone: StatusTone.success,
    text: qualifier ? __t('Ativo · %{quando}', { quando: qualifier }) : __t('Ativo'),
  }
}
